import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { esignatureService } from "../../services/esignature";
import { getApiEndpoint } from "../../config/config";
import {
  formatPhoneNumber,
  formatNumberWithCommas,
  formatPercentage,
  formatEmail,
} from "../../utils/formatters";
import "./SendDocsModal.css";
import { handleAuthError, handleGenericError } from "../../utils/errorHandler";

function SendDocsModal({
  isOpen,
  onClose,
  recruit,
  onDocumentSent,
  preSelectedTemplateId,
}) {
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();
  const { getVerifiedJWT } = useAuth();
  const { showSuccess, showError } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [sending, setSending] = useState(false);
  const [reps, setReps] = useState([]);

  // Recipient information
  const [recipientInfo, setRecipientInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    recruitedBy: "",
    manager: "",
    isManager: false,
    instagram: "",
    linkedin: "",
    recruitingSourceNotes: "",
  });

  // Compensation data
  const [compensationType, setCompensationType] = useState("none");
  const [compensationData, setCompensationData] = useState({
    commissionScale: Array.from({ length: 10 }, (_, index) => ({
      id: index + 1,
      revenue: "",
      percentage: "",
    })),
    overrideScale: Array.from({ length: 10 }, (_, index) => ({
      id: index + 1,
      revenue: "",
      percentage: "",
    })),
    marketingDealScale: Array.from({ length: 5 }, (_, index) => ({
      id: index + 1,
      revenue: "",
      percentage: "",
    })),
  });

  // Default scale templates
  const [commissionTemplates, setCommissionTemplates] = useState([]);
  const [overrideTemplates, setOverrideTemplates] = useState([]);
  const [marketingTemplates, setMarketingTemplates] = useState([]);
  const [selectedCommissionTemplate, setSelectedCommissionTemplate] =
    useState("");
  const [selectedOverrideTemplate, setSelectedOverrideTemplate] = useState("");
  const [selectedMarketingTemplate, setSelectedMarketingTemplate] =
    useState("");
  const [showOptionalFields, setShowOptionalFields] = useState(false);

  // Get all reps for recruited_by dropdown
  const recruiters = reps.map(rep => ({
    id: rep.rep_id,
    name: `${rep.first_name} ${rep.last_name}`,
    ...rep,
  }));

  // Get only managers for manager dropdown
  const managers = reps
    .filter(rep => rep.is_manager)
    .map(rep => ({
      id: rep.rep_id,
      name: `${rep.first_name} ${rep.last_name}`,
      ...rep,
    }));

  // Get selected manager details
  const selectedManager = managers.find(
    m => m.id === parseInt(recipientInfo.manager)
  );

  const loadReps = async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(
        getApiEndpoint("/v1/company-downline/reps-by-manager"),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        const data = result.data;
        setReps(data.reps);
      }
    } catch (err) {}
  };

  const loadDefaultScaleTemplates = async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      // Load commission templates
      const commissionResponse = await fetch(
        getApiEndpoint("/v1/scales/default-scales/COMMISSION"),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );
      if (commissionResponse.ok) {
        const commissionData = await commissionResponse.json();
        setCommissionTemplates(commissionData.data || []);
      }

      // Load override templates
      const overrideResponse = await fetch(
        getApiEndpoint("/v1/scales/default-scales/OVERRIDE"),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );
      if (overrideResponse.ok) {
        const overrideData = await overrideResponse.json();
        setOverrideTemplates(overrideData.data || []);
      }

      // Load marketing templates
      const marketingResponse = await fetch(
        getApiEndpoint("/v1/scales/default-scales/MD"),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );
      if (marketingResponse.ok) {
        const marketingData = await marketingResponse.json();
        setMarketingTemplates(marketingData.data || []);
      }
    } catch (err) {
      // Templates are optional - silently handle error
    }
  };

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        handleAuthError("Invalid jwt");
        setLoading(false);
        return;
      }

      const result = await esignatureService.getTemplates(jwt);

      // Ensure we always set an array - match the logic from TemplateList
      let templatesArray = [];
      if (Array.isArray(result)) {
        templatesArray = result;
      } else if (result && Array.isArray(result.data)) {
        templatesArray = result.data;
      } else if (result && Array.isArray(result.templates)) {
        templatesArray = result.templates;
      } else if (
        result &&
        result.data &&
        Array.isArray(result.data.templates)
      ) {
        templatesArray = result.data.templates;
      } else if (result && typeof result === "object") {
        // Try to extract templates from object values if it's an object
        const values = Object.values(result.data || result);
        if (values.length > 0 && values[0] && typeof values[0] === "object") {
          templatesArray = values;
        } else {
          templatesArray = [];
        }
      }

      setTemplates(templatesArray);
    } catch (error) {
      handleGenericError(error, "Failed to load templates");
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadReps();
      fetchTemplates();
      loadDefaultScaleTemplates();
    } else {
      // Reset state when modal closes
      resetState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Effect to populate basic recruit info immediately when modal opens
  useEffect(() => {
    if (isOpen && recruit) {
      // Handle both entity field names (first_name, last_name) and composite name field
      let firstName = "";
      let lastName = "";

      if (recruit.first_name || recruit.last_name) {
        // Use entity fields directly if available
        firstName = recruit.first_name || "";
        lastName = recruit.last_name || "";
      } else if (recruit.name) {
        // Fall back to parsing composite name field
        const nameParts = recruit.name.trim().split(/\s+/);
        firstName = nameParts[0] || "";
        lastName = nameParts.slice(1).join(" ") || "";
      }

      setRecipientInfo(prev => ({
        ...prev,
        firstName: firstName,
        lastName: lastName,
        email: recruit.email || "",
        phoneNumber: recruit.phone_number || recruit.phone || "", // Support both field names
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, recruit]);

  // Separate effect to populate recruited_by and manager once reps are loaded
  useEffect(() => {
    if (isOpen && recruit && reps.length > 0) {
      // Find recruited by rep (match by recruiter_name)
      const recruiterRep = reps.find(rep => {
        const repFullName = `${rep.first_name} ${rep.last_name}`;
        return repFullName === recruit.recruiter_name;
      });

      // Find manager (match by manager_user_season_id or recruiter_name if they're the manager)
      let managerRep = null;
      if (recruit.manager_user_season_id) {
        managerRep = reps.find(
          rep =>
            rep.user_season_id === recruit.manager_user_season_id &&
            rep.is_manager
        );
      }
      // If no match by ID, try matching by name for managers
      if (!managerRep && recruit.recruiter_name) {
        managerRep = reps.find(rep => {
          const repFullName = `${rep.first_name} ${rep.last_name}`;
          return repFullName === recruit.recruiter_name && rep.is_manager;
        });
      }

      setRecipientInfo(prev => ({
        ...prev,
        recruitedBy: recruiterRep ? String(recruiterRep.rep_id) : "",
        manager: managerRep ? String(managerRep.rep_id) : "",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, recruit, reps]);

  // Auto-select template when preSelectedTemplateId is provided
  useEffect(() => {
    if (
      isOpen &&
      preSelectedTemplateId &&
      templates.length > 0 &&
      !selectedTemplate
    ) {
      const template = templates.find(
        t => t.id === parseInt(preSelectedTemplateId)
      );
      if (template) {
        setSelectedTemplate(template);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, preSelectedTemplateId, templates, selectedTemplate]);

  const resetState = () => {
    setCurrentStep(1);
    setSelectedTemplate(null);
    setTemplates([]);
    setRecipientInfo({
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      recruitedBy: "",
      manager: "",
      isManager: false,
      instagram: "",
      linkedin: "",
      recruitingSourceNotes: "",
    });
    setCompensationType("none");
    setCompensationData({
      commissionScale: Array.from({ length: 10 }, (_, index) => ({
        id: index + 1,
        revenue: "",
        percentage: "",
      })),
      overrideScale: Array.from({ length: 10 }, (_, index) => ({
        id: index + 1,
        revenue: "",
        percentage: "",
      })),
      marketingDealScale: Array.from({ length: 5 }, (_, index) => ({
        id: index + 1,
        revenue: "",
        percentage: "",
      })),
    });
    setSelectedCommissionTemplate("");
    setSelectedOverrideTemplate("");
    setSelectedMarketingTemplate("");
  };

  const handleInputChange = e => {
    const { name, value, type, checked } = e.target;

    if (name === "phoneNumber") {
      const formattedValue = formatPhoneNumber(value);
      setRecipientInfo(prev => ({
        ...prev,
        [name]: formattedValue,
      }));
    } else if (name === "email") {
      const formattedValue = formatEmail(value);
      setRecipientInfo(prev => ({
        ...prev,
        [name]: formattedValue,
      }));
    } else {
      setRecipientInfo(prev => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const validateForm = () => {
    const { firstName, lastName, email, phoneNumber, recruitedBy, manager } =
      recipientInfo;

    if (!firstName.trim()) {
      showError("First name is required");
      return false;
    }
    if (!lastName.trim()) {
      showError("Last name is required");
      return false;
    }
    if (!email.trim()) {
      showError("Email is required");
      return false;
    }
    if (!recruitedBy) {
      showError("Recruited By is required");
      return false;
    }
    if (!manager) {
      showError("Manager is required");
      return false;
    }
    if (!selectedTemplate) {
      showError("Please select an agreement");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showError("Please enter a valid email address");
      return false;
    }

    // Validate phone number format if provided (optional)
    if (phoneNumber && phoneNumber.trim()) {
      const cleanPhone = phoneNumber.replace(/\D/g, "");
      if (cleanPhone.length !== 10 && cleanPhone.length !== 11) {
        showError("Please enter a valid phone number (10-11 digits)");
        return false;
      }
    }

    return true;
  };

  const handleTemplateSelect = template => {
    setSelectedTemplate(template);
  };

  const handleNextToCompensation = () => {
    if (!validateForm()) {
      return;
    }
    setCurrentStep(2);
  };

  const handleBackToRecipientInfo = () => {
    setCurrentStep(1);
  };

  const handleCompensationTypeChange = type => {
    setCompensationType(type);
  };

  const handleCommissionTemplateChange = e => {
    const templateId = e.target.value;
    setSelectedCommissionTemplate(templateId);

    if (templateId) {
      const template = commissionTemplates.find(
        t => t.id === parseInt(templateId)
      );
      if (template && template.scale_lines) {
        const newCommissionScale = template.scale_lines.map((line, index) => ({
          id: index + 1,
          revenue: formatNumberWithCommas(line.revenue_amount.toString()),
          percentage: formatPercentage(line.percentage.toString()),
        }));
        setCompensationData(prev => ({
          ...prev,
          commissionScale: newCommissionScale,
        }));
      }
    }
  };

  const handleOverrideTemplateChange = e => {
    const templateId = e.target.value;
    setSelectedOverrideTemplate(templateId);

    if (templateId) {
      const template = overrideTemplates.find(
        t => t.id === parseInt(templateId)
      );
      if (template && template.scale_lines) {
        const newOverrideScale = template.scale_lines.map((line, index) => ({
          id: index + 1,
          revenue: formatNumberWithCommas(line.revenue_amount.toString()),
          percentage: formatPercentage(line.percentage.toString()),
        }));
        setCompensationData(prev => ({
          ...prev,
          overrideScale: newOverrideScale,
        }));
      }
    }
  };

  const handleMarketingTemplateChange = e => {
    const templateId = e.target.value;
    setSelectedMarketingTemplate(templateId);

    if (templateId) {
      const template = marketingTemplates.find(
        t => t.id === parseInt(templateId)
      );
      if (template && template.scale_lines) {
        const newMarketingScale = template.scale_lines.map((line, index) => ({
          id: index + 1,
          revenue: formatNumberWithCommas(line.revenue_amount.toString()),
          percentage: formatPercentage(line.percentage.toString()),
        }));
        setCompensationData(prev => ({
          ...prev,
          marketingDealScale: newMarketingScale,
        }));
      }
    }
  };

  const handleCommissionChange = (id, field, value) => {
    const formattedValue =
      field === "revenue"
        ? formatNumberWithCommas(value)
        : field === "percentage"
          ? formatPercentage(value)
          : value;
    setCompensationData(prev => ({
      ...prev,
      commissionScale: prev.commissionScale.map(item =>
        item.id === id ? { ...item, [field]: formattedValue } : item
      ),
    }));
  };

  const handleOverrideChange = (id, field, value) => {
    const formattedValue =
      field === "revenue"
        ? formatNumberWithCommas(value)
        : field === "percentage"
          ? formatPercentage(value)
          : value;
    setCompensationData(prev => ({
      ...prev,
      overrideScale: prev.overrideScale.map(item =>
        item.id === id ? { ...item, [field]: formattedValue } : item
      ),
    }));
  };

  const handleMarketingChange = (id, field, value) => {
    const formattedValue =
      field === "revenue"
        ? formatNumberWithCommas(value)
        : field === "percentage"
          ? formatPercentage(value)
          : value;
    setCompensationData(prev => ({
      ...prev,
      marketingDealScale: prev.marketingDealScale.map(item =>
        item.id === id ? { ...item, [field]: formattedValue } : item
      ),
    }));
  };

  const addCommissionRow = () => {
    if (compensationData.commissionScale.length >= 16) return;
    const newId =
      Math.max(...compensationData.commissionScale.map(item => item.id)) + 1;
    setCompensationData(prev => ({
      ...prev,
      commissionScale: [
        ...prev.commissionScale,
        { id: newId, revenue: "", percentage: "" },
      ],
    }));
  };

  const removeCommissionRow = id => {
    if (compensationData.commissionScale.length > 1) {
      setCompensationData(prev => ({
        ...prev,
        commissionScale: prev.commissionScale.filter(item => item.id !== id),
      }));
    }
  };

  const addOverrideRow = () => {
    if (compensationData.overrideScale.length >= 16) return;
    const newId =
      Math.max(...compensationData.overrideScale.map(item => item.id)) + 1;
    setCompensationData(prev => ({
      ...prev,
      overrideScale: [
        ...prev.overrideScale,
        { id: newId, revenue: "", percentage: "" },
      ],
    }));
  };

  const removeOverrideRow = id => {
    if (compensationData.overrideScale.length > 1) {
      setCompensationData(prev => ({
        ...prev,
        overrideScale: prev.overrideScale.filter(item => item.id !== id),
      }));
    }
  };

  const addMarketingRow = () => {
    if (compensationData.marketingDealScale.length >= 16) return;
    const newId =
      Math.max(...compensationData.marketingDealScale.map(item => item.id)) + 1;
    setCompensationData(prev => ({
      ...prev,
      marketingDealScale: [
        ...prev.marketingDealScale,
        { id: newId, revenue: "", percentage: "" },
      ],
    }));
  };

  const removeMarketingRow = id => {
    if (compensationData.marketingDealScale.length > 1) {
      setCompensationData(prev => ({
        ...prev,
        marketingDealScale: prev.marketingDealScale.filter(
          item => item.id !== id
        ),
      }));
    }
  };

  const formatCompensationData = () => {
    // Helper to strip formatting and convert to number
    const parseNumber = value => {
      if (!value) return 0;
      const cleaned = value.toString().replace(/[,$%\s]/g, "");
      return parseFloat(cleaned) || 0;
    };

    // Filter and format commission scale (required - always included)
    const commissionScale = compensationData.commissionScale
      .filter(line => {
        const revenue = parseNumber(line.revenue);
        const percentage = parseNumber(line.percentage);
        return revenue > 0 && percentage > 0;
      })
      .map(line => ({
        revenue_amount: parseNumber(line.revenue),
        percentage: parseNumber(line.percentage),
      }));

    // Build compensation data object
    const compensationPayload = {
      compensation_type: compensationType,
      commission_scale: commissionScale,
    };

    // Add override scale if type is 'override'
    if (compensationType === "override") {
      const overrideScale = compensationData.overrideScale
        .filter(line => {
          const revenue = parseNumber(line.revenue);
          const percentage = parseNumber(line.percentage);
          return revenue > 0 && percentage > 0;
        })
        .map(line => ({
          revenue_amount: parseNumber(line.revenue),
          percentage: parseNumber(line.percentage),
        }));

      if (overrideScale.length > 0) {
        compensationPayload.override_scale = overrideScale;
      }
    }

    // Add marketing scale if type is 'marketing'
    if (compensationType === "marketing") {
      const marketingScale = compensationData.marketingDealScale
        .filter(line => {
          const revenue = parseNumber(line.revenue);
          const percentage = parseNumber(line.percentage);
          return revenue > 0 && percentage > 0;
        })
        .map(line => ({
          revenue_amount: parseNumber(line.revenue),
          percentage: parseNumber(line.percentage),
        }));

      if (marketingScale.length > 0) {
        compensationPayload.marketing_scale = marketingScale;
      }
    }

    return compensationPayload;
  };

  const handlePreviewDocument = async () => {
    if (!validateForm()) {
      return;
    }

    setSending(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        handleAuthError("Invalid jwt");
        return;
      }

      const fullName =
        `${recipientInfo.firstName} ${recipientInfo.lastName}`.trim();

      const documentData = {
        title: `${selectedTemplate.name} - ${fullName}`,
        recruiting_lead_id:
          recruit?.id || recruit?.lead_id || recruit?.leadId
            ? parseInt(recruit.id || recruit.lead_id || recruit.leadId)
            : undefined, // Link document to specific recruiting lead
        compensation_data: formatCompensationData(), // Include compensation scales
      };

      const createResult = await esignatureService.createDocumentFromTemplate(
        selectedTemplate.id,
        documentData,
        jwt
      );

      const documentId = createResult.data?.id || createResult.id;

      if (!documentId) {
        throw new Error("Failed to get document ID from response");
      }

      // Add both Direct Seller and Guarantor as recipients
      const recipients = [
        {
          email: recipientInfo.email,
          full_name: fullName,
          role: "signer",
          signing_order: 1,
        },
      ];

      if (selectedManager) {
        recipients.push({
          email: selectedManager.email,
          full_name: selectedManager.name,
          role: "signer",
          signing_order: 2,
        });
      }

      await esignatureService.addRecipients(documentId, recipients, jwt);

      handleClose();
      showSuccess("Document prepared successfully!");
    } catch (error) {
      handleGenericError(error, "Failed to prepare document");
    } finally {
      setSending(false);
    }
  };

  const handleSendDocument = async () => {
    if (!validateForm()) {
      return;
    }

    setSending(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        handleAuthError("Invalid jwt");
        return;
      }

      const fullName =
        `${recipientInfo.firstName} ${recipientInfo.lastName}`.trim();

      const documentData = {
        title: `${selectedTemplate.name} - ${fullName}`,
        recruiting_lead_id:
          recruit?.id || recruit?.lead_id || recruit?.leadId
            ? parseInt(recruit.id || recruit.lead_id || recruit.leadId)
            : undefined, // Link document to specific recruiting lead
        compensation_data: formatCompensationData(), // Include compensation scales
      };

      const createResult = await esignatureService.createDocumentFromTemplate(
        selectedTemplate.id,
        documentData,
        jwt
      );

      const documentId = createResult.data?.id || createResult.id;

      if (!documentId) {
        throw new Error("Failed to get document ID from response");
      }

      // Add both Direct Seller and Guarantor as recipients
      const recipients = [
        {
          email: recipientInfo.email,
          full_name: fullName,
          role: "signer",
          signing_order: 1,
        },
      ];

      if (selectedManager) {
        recipients.push({
          email: selectedManager.email,
          full_name: selectedManager.name,
          role: "signer",
          signing_order: 2,
        });
      }

      await esignatureService.addRecipients(documentId, recipients, jwt);

      // Actually send the document
      await esignatureService.sendDocument(documentId, jwt);

      // Notify parent that document was sent successfully
      if (onDocumentSent && recruit) {
        onDocumentSent(recruit);
      }

      handleClose(true); // Pass true to indicate document was sent
      showSuccess("Document sent successfully!");
    } catch (error) {
      handleGenericError(error, "Failed to send document");
    } finally {
      setSending(false);
    }
  };

  const handleClose = (documentWasSent = false) => {
    resetState();
    onClose(documentWasSent);
  };

  if (!isOpen) return null;

  return (
    <div className="send-docs-modal-overlay" onClick={handleClose}>
      <div
        className="send-docs-modal-content fullscreen"
        onClick={e => e.stopPropagation()}
      >
        <div className="send-docs-modal-header">
          <h2>Send Document</h2>
          <button className="send-docs-close-btn" onClick={handleClose}>
            ×
          </button>
        </div>

        <div className="send-docs-modal-body fullscreen">
          {currentStep === 1 ? (
            <>
              <div className="recipient-info-section">
                <div className="recipient-info-header">
                  <h3>Recipient Information</h3>
                  <p className="recipient-info-description">
                    Enter the recipient's information
                  </p>
                </div>

                <div className="recipient-info-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="firstName">
                        First Name <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={recipientInfo.firstName}
                        onChange={handleInputChange}
                        placeholder="Enter first name"
                        required
                        disabled={sending}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="lastName">
                        Last Name <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={recipientInfo.lastName}
                        onChange={handleInputChange}
                        placeholder="Enter last name"
                        required
                        disabled={sending}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="email">
                        Email <span className="required">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={recipientInfo.email}
                        onChange={handleInputChange}
                        placeholder="Enter email address"
                        required
                        disabled={sending}
                      />
                    </div>

                    <div className="form-group">
                      <label
                        style={{ marginBottom: "0.5rem", display: "block" }}
                      >
                        &nbsp;
                      </label>
                      <div className="manager-privileges-box">
                        <div className="manager-privileges-content">
                          <div>
                            <div className="manager-privileges-title">
                              Has Manager Privileges?
                            </div>
                            <div className="manager-privileges-subtitle">
                              Grant this rep manager-level access
                            </div>
                          </div>
                          <label className="toggle-switch">
                            <input
                              type="checkbox"
                              id="isManager"
                              name="isManager"
                              checked={recipientInfo.isManager}
                              onChange={handleInputChange}
                              disabled={sending}
                            />
                            <span className="toggle-slider"></span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="recruitedBy">
                        Recruited By <span className="required">*</span>
                      </label>
                      <select
                        id="recruitedBy"
                        name="recruitedBy"
                        value={recipientInfo.recruitedBy}
                        onChange={handleInputChange}
                        required
                        disabled={sending}
                      >
                        <option value="">Select recruiter...</option>
                        {recruiters.map(recruiter => (
                          <option key={recruiter.id} value={recruiter.id}>
                            {recruiter.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="manager">
                        Manager <span className="required">*</span>
                      </label>
                      <select
                        id="manager"
                        name="manager"
                        value={recipientInfo.manager}
                        onChange={handleInputChange}
                        required
                        disabled={sending}
                      >
                        <option value="">Select manager...</option>
                        {managers.map(manager => (
                          <option key={manager.id} value={manager.id}>
                            {manager.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Optional Section - Collapsible */}
                  <div className="optional-section">
                    <div
                      className="optional-section-header"
                      onClick={() => setShowOptionalFields(!showOptionalFields)}
                      style={{ cursor: "pointer", marginBottom: "1rem" }}
                    >
                      <h4 style={{ margin: 0, display: "inline-block" }}>
                        Optional
                      </h4>
                      <span style={{ marginLeft: "0.5rem" }}>
                        {showOptionalFields ? "▲" : "▼"}
                      </span>
                    </div>

                    {showOptionalFields && (
                      <div className="optional-fields">
                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor="instagram">Instagram</label>
                            <input
                              type="text"
                              id="instagram"
                              name="instagram"
                              value={recipientInfo.instagram || ""}
                              onChange={handleInputChange}
                              placeholder="@username or profile URL"
                              disabled={sending}
                            />
                          </div>

                          <div className="form-group">
                            <label htmlFor="linkedin">LinkedIn</label>
                            <input
                              type="text"
                              id="linkedin"
                              name="linkedin"
                              value={recipientInfo.linkedin || ""}
                              onChange={handleInputChange}
                              placeholder="LinkedIn profile URL"
                              disabled={sending}
                            />
                          </div>
                        </div>

                        <div className="form-group">
                          <label htmlFor="phoneNumber">Phone Number</label>
                          <input
                            type="tel"
                            id="phoneNumber"
                            name="phoneNumber"
                            value={recipientInfo.phoneNumber}
                            onChange={handleInputChange}
                            placeholder="(000) 000-0000"
                            disabled={sending}
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="recruitingSourceNotes">
                            Recruiting Source Notes
                          </label>
                          <textarea
                            id="recruitingSourceNotes"
                            name="recruitingSourceNotes"
                            value={recipientInfo.recruitingSourceNotes || ""}
                            onChange={handleInputChange}
                            placeholder="Enter recruiting source notes..."
                            rows={3}
                            disabled={sending}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="template-selection-section">
                <div className="template-selection-header">
                  <h3>Select Agreement</h3>
                  <p className="template-selection-description">
                    Choose which agreement to send
                  </p>
                </div>

                <div className="templates-container">
                  {loading ? (
                    <div className="loading-templates">
                      <p>Loading agreements...</p>
                    </div>
                  ) : templates.length === 0 ? (
                    <div className="no-templates">
                      <p>No agreements available</p>
                      <p className="no-templates-hint">
                        Create templates in the E-Signature section first
                      </p>
                    </div>
                  ) : selectedTemplate ? (
                    <>
                      <div className="selected-template-display">
                        <div className="template-card selected-only">
                          <div className="template-header-row">
                            <div className="template-icon">
                              <span className="material-icons">
                                description
                              </span>
                            </div>
                            <h4 className="template-name">
                              {selectedTemplate.name}
                            </h4>
                            <button
                              className="change-template-btn"
                              onClick={() => setSelectedTemplate(null)}
                            >
                              Change
                            </button>
                          </div>
                        </div>

                        {recipientInfo.manager && (
                          <div className="document-roles-inline">
                            <h4>Document Roles</h4>
                            <div className="roles-grid-inline">
                              <div className="role-card-inline">
                                <div className="role-label-inline">
                                  Direct Seller
                                </div>
                                <div className="role-name-inline">
                                  {recipientInfo.firstName}{" "}
                                  {recipientInfo.lastName}
                                </div>
                                <div className="role-email-inline">
                                  {recipientInfo.email}
                                </div>
                                <div className="role-phone-inline">
                                  {recipientInfo.phoneNumber}
                                </div>
                              </div>
                              {selectedManager && (
                                <div className="role-card-inline">
                                  <div className="role-label-inline">
                                    Guarantor (Manager)
                                  </div>
                                  <div className="role-name-inline">
                                    {selectedManager.name}
                                  </div>
                                  <div className="role-email-inline">
                                    {selectedManager.email || "N/A"}
                                  </div>
                                  <div className="role-phone-inline">
                                    {formatPhoneNumber(
                                      selectedManager.phone_number || ""
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="templates-list">
                      {templates.map(template => (
                        <div
                          key={template.id}
                          className="template-card"
                          onClick={() =>
                            !sending && handleTemplateSelect(template)
                          }
                        >
                          <div className="template-header-row">
                            <div className="template-icon">
                              <span className="material-icons">
                                description
                              </span>
                            </div>
                            <h4 className="template-name">{template.name}</h4>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="compensation-step-container">
                <div className="compensation-header">
                  <h3>Compensation</h3>
                  <div className="compensation-type-toggles">
                    <button
                      type="button"
                      className={`toggle-btn ${compensationType === "none" ? "active" : ""}`}
                      onClick={() => handleCompensationTypeChange("none")}
                    >
                      No Override
                    </button>
                    <button
                      type="button"
                      className={`toggle-btn ${compensationType === "override" ? "active" : ""}`}
                      onClick={() => handleCompensationTypeChange("override")}
                    >
                      Override
                    </button>
                    <button
                      type="button"
                      className={`toggle-btn ${compensationType === "marketing" ? "active" : ""}`}
                      onClick={() => handleCompensationTypeChange("marketing")}
                    >
                      Marketing Deal
                    </button>
                  </div>
                </div>

                <div className="compensation-grid">
                  <div className="compensation-column">
                    <h4>Pay Scale</h4>

                    {commissionTemplates.length > 0 && (
                      <div className="form-group">
                        <label>Use Default Scale Template</label>
                        <select
                          value={selectedCommissionTemplate}
                          onChange={handleCommissionTemplateChange}
                        >
                          <option value="">Select a template...</option>
                          {commissionTemplates.map(template => (
                            <option key={template.id} value={template.id}>
                              {template.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className="commission-table-container">
                      <table className="commission-table">
                        <thead>
                          <tr>
                            <th>Percentage (%)</th>
                            <th>Revenue ($)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {compensationData.commissionScale.map(item => (
                            <tr key={item.id}>
                              <td>
                                <input
                                  type="text"
                                  value={item.percentage}
                                  onChange={e =>
                                    handleCommissionChange(
                                      item.id,
                                      "percentage",
                                      e.target.value
                                    )
                                  }
                                  placeholder="0.0"
                                />
                              </td>
                              <td className="percentage-cell">
                                <input
                                  type="text"
                                  value={item.revenue}
                                  onChange={e =>
                                    handleCommissionChange(
                                      item.id,
                                      "revenue",
                                      e.target.value
                                    )
                                  }
                                  placeholder="0.00"
                                />
                                {compensationData.commissionScale.length >
                                  1 && (
                                  <button
                                    type="button"
                                    className="simple-remove-btn"
                                    onClick={() => removeCommissionRow(item.id)}
                                    title="Remove row"
                                  >
                                    <span className="material-icons">
                                      close
                                    </span>
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={addCommissionRow}
                      disabled={compensationData.commissionScale.length >= 16}
                    >
                      + Add Row{" "}
                      {compensationData.commissionScale.length >= 16
                        ? "(Max 16)"
                        : ""}
                    </button>
                  </div>

                  <div className="compensation-column">
                    <h4>
                      {compensationType === "override"
                        ? "Override Scale"
                        : compensationType === "marketing"
                          ? "Marketing Deal Scale"
                          : "Override / Marketing Deal"}
                    </h4>

                    <div
                      className={`secondary-scale ${compensationType === "none" ? "disabled" : ""}`}
                    >
                      {compensationType === "override" &&
                        overrideTemplates.length > 0 && (
                          <div className="form-group">
                            <label>Use Default Override Template</label>
                            <select
                              value={selectedOverrideTemplate}
                              onChange={handleOverrideTemplateChange}
                              disabled={compensationType === "none"}
                            >
                              <option value="">Select a template...</option>
                              {overrideTemplates.map(template => (
                                <option key={template.id} value={template.id}>
                                  {template.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                      {compensationType === "marketing" &&
                        marketingTemplates.length > 0 && (
                          <div className="form-group">
                            <label>Use Default Marketing Template</label>
                            <select
                              value={selectedMarketingTemplate}
                              onChange={handleMarketingTemplateChange}
                              disabled={compensationType === "none"}
                            >
                              <option value="">Select a template...</option>
                              {marketingTemplates.map(template => (
                                <option key={template.id} value={template.id}>
                                  {template.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                      <div className="commission-table-container">
                        <table className="commission-table">
                          <thead>
                            <tr>
                              <th>Percentage (%)</th>
                              <th>Revenue ($)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {compensationType === "override" &&
                              compensationData.overrideScale.map(item => (
                                <tr key={item.id}>
                                  <td>
                                    <input
                                      type="text"
                                      value={item.percentage}
                                      onChange={e =>
                                        handleOverrideChange(
                                          item.id,
                                          "percentage",
                                          e.target.value
                                        )
                                      }
                                      placeholder="0.0"
                                      disabled={compensationType === "none"}
                                    />
                                  </td>
                                  <td className="percentage-cell">
                                    <input
                                      type="text"
                                      value={item.revenue}
                                      onChange={e =>
                                        handleOverrideChange(
                                          item.id,
                                          "revenue",
                                          e.target.value
                                        )
                                      }
                                      placeholder="0.00"
                                      disabled={compensationType === "none"}
                                    />
                                    {compensationData.overrideScale.length >
                                      1 && (
                                      <button
                                        type="button"
                                        className="simple-remove-btn"
                                        onClick={() =>
                                          removeOverrideRow(item.id)
                                        }
                                        title="Remove row"
                                        disabled={compensationType === "none"}
                                      >
                                        <span className="material-icons">
                                          close
                                        </span>
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              ))}

                            {compensationType === "marketing" &&
                              compensationData.marketingDealScale.map(item => (
                                <tr key={item.id}>
                                  <td>
                                    <input
                                      type="text"
                                      value={item.percentage}
                                      onChange={e =>
                                        handleMarketingChange(
                                          item.id,
                                          "percentage",
                                          e.target.value
                                        )
                                      }
                                      placeholder="0.0"
                                      disabled={compensationType === "none"}
                                    />
                                  </td>
                                  <td className="percentage-cell">
                                    <input
                                      type="text"
                                      value={item.revenue}
                                      onChange={e =>
                                        handleMarketingChange(
                                          item.id,
                                          "revenue",
                                          e.target.value
                                        )
                                      }
                                      placeholder="0.00"
                                      disabled={compensationType === "none"}
                                    />
                                    {compensationData.marketingDealScale
                                      .length > 1 && (
                                      <button
                                        type="button"
                                        className="simple-remove-btn"
                                        onClick={() =>
                                          removeMarketingRow(item.id)
                                        }
                                        title="Remove row"
                                        disabled={compensationType === "none"}
                                      >
                                        <span className="material-icons">
                                          close
                                        </span>
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>

                      {compensationType === "override" && (
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={addOverrideRow}
                          disabled={compensationData.overrideScale.length >= 16}
                        >
                          + Add Row{" "}
                          {compensationData.overrideScale.length >= 16
                            ? "(Max 16)"
                            : ""}
                        </button>
                      )}

                      {compensationType === "marketing" && (
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={addMarketingRow}
                          disabled={
                            compensationData.marketingDealScale.length >= 16
                          }
                        >
                          + Add Row{" "}
                          {compensationData.marketingDealScale.length >= 16
                            ? "(Max 16)"
                            : ""}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="send-docs-modal-footer">
          <button
            className="btn btn-secondary back-button-left"
            onClick={
              currentStep === 1 ? handleClose : handleBackToRecipientInfo
            }
            disabled={sending}
          >
            {currentStep === 1 ? "Cancel" : "Back"}
          </button>
          {currentStep === 2 && (
            <>
              <button
                className="btn btn-outline-primary"
                onClick={handlePreviewDocument}
                disabled={sending}
              >
                {sending ? "Preparing..." : "Advanced Preview Document"}
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSendDocument}
                disabled={sending}
              >
                {sending ? "Sending..." : "Send Document"}
              </button>
            </>
          )}
          {currentStep === 1 && (
            <button
              className="btn btn-primary"
              onClick={handleNextToCompensation}
              disabled={sending}
            >
              Next: Compensation
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default SendDocsModal;
